import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import { ActivatedRoute } from '@angular/router';
import { environment } from '../../../environments/environment';

interface SurveyData {
  q1?: boolean;
  q2?: number;
  q3?: boolean;
  q4?: boolean;
  q5?: number;
  q6?: string;
}

interface ApplicationStatusResponse {
  success?: boolean;
  data?: {
    applied?: number | boolean;
    withdrawn?: number | boolean;
  };
}

@Component({
  selector: 'app-survey',
  imports: [CommonModule, FormsModule, HttpClientModule],
  templateUrl: './survey.html',
  styleUrl: './survey.scss',
})
export class Survey implements OnInit, OnDestroy {
  surveyId: string = '';
  surveyData: SurveyData = {};
  isLoading: boolean = false;
  isInitialLoading: boolean = false;
  errorMessage: string = '';
  successMessage: string = '';
  hasRecordedAnswers: boolean = false;

  // Application state
  isApplicationLoading: boolean = false;
  applicationApplied: boolean = false;
  applicationWithdrawn: boolean = false;
  showWithdrawConfirmModal: boolean = false;
  applicationError: string = '';

  // Toast notification state
  showToast: boolean = false;
  toastMessage: string = '';
  toastType: 'success' | 'error' = 'success';

  private readonly surveySaveDelayMs = 3000;
  private surveySaveTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(private route: ActivatedRoute, private http: HttpClient) {}

  ngOnInit() {
    this.route.params.subscribe((params) => {
      this.surveyId = params['id'];
      if (this.surveyId) {
        this.loadSurvey();
        this.loadApplicationStatus();
      }
    });
  }

  ngOnDestroy() {
    if (this.surveySaveTimer) {
      clearTimeout(this.surveySaveTimer);
      this.surveySaveTimer = null;
      // Flush any pending answer so the last edit is not lost on navigate away.
      this.sendSurveyUpdate();
    }
  }

  loadSurvey() {
    this.isInitialLoading = true;
    this.errorMessage = '';

    const headers = {
      'x-client-id': this.surveyId,
      'Content-Type': 'application/json',
    };

    this.http.get(`${environment.apiUrl}/survey`, { headers }).subscribe({
      next: (response: any) => {
        this.isInitialLoading = false;
        if (
          response.success &&
          response.data &&
          response.data.surveyResponses
        ) {
          const rawData = response.data.surveyResponses;
          // Convert database values to proper JavaScript types
          this.surveyData = {
            q1: rawData.q1 === 1 ? true : rawData.q1 === 0 ? false : undefined,
            q2:
              rawData.q2 !== null && rawData.q2 !== undefined
                ? Number(rawData.q2)
                : undefined,
            q3: rawData.q3 === 1 ? true : rawData.q3 === 0 ? false : undefined,
            q4: rawData.q4 === 1 ? true : rawData.q4 === 0 ? false : undefined,
            q5:
              rawData.q5 !== null && rawData.q5 !== undefined
                ? parseFloat(rawData.q5)
                : undefined,
            q6: rawData.q6,
          };

          // Check if any answers have been recorded
          this.hasRecordedAnswers = this.checkIfAnswersRecorded();
        }
      },
      error: (error) => {
        this.isInitialLoading = false;
        this.errorMessage =
          error.error?.message || 'Failed to load survey data';
        console.error('Survey load error:', error);
      },
    });
  }

  // Application status loader
  loadApplicationStatus() {
    this.isApplicationLoading = true;
    this.applicationError = '';

    const headers = {
      'x-client-id': this.surveyId,
      'Content-Type': 'application/json',
    };

    this.http.get(`${environment.apiUrl}/application`, { headers }).subscribe({
      next: (response: ApplicationStatusResponse) => {
        this.isApplicationLoading = false;
        const appliedRaw = response?.data?.applied;
        const withdrawnRaw = response?.data?.withdrawn;
        this.applicationApplied = appliedRaw === true || appliedRaw === 1;
        this.applicationWithdrawn = withdrawnRaw === true || withdrawnRaw === 1;
      },
      error: (error) => {
        this.isApplicationLoading = false;
        // Non-fatal for the survey; show local error but keep page usable
        this.applicationError =
          error.error?.message || 'Failed to load application status';
        console.error('Application status load error:', error);
      },
    });
  }

  // Toast notification methods
  showToastNotification(
    message: string,
    type: 'success' | 'error' = 'success'
  ) {
    this.toastMessage = message;
    this.toastType = type;
    this.showToast = true;

    // Auto-hide after 4 seconds
    setTimeout(() => {
      this.hideToast();
    }, 4000);
  }

  hideToast() {
    this.showToast = false;
  }

  // Application actions
  applyForBeta() {
    if (this.isApplicationLoading || this.applicationApplied) return;
    this.isApplicationLoading = true;
    this.applicationError = '';

    const headers = {
      'x-client-id': this.surveyId,
      'Content-Type': 'application/json',
    };

    this.http
      .post(`${environment.apiUrl}/application`, {}, { headers })
      .subscribe({
        next: () => {
          this.isApplicationLoading = false;
          this.applicationApplied = true;
          this.applicationWithdrawn = false;
          this.showToastNotification('Application submitted successfully!');
        },
        error: (error) => {
          this.isApplicationLoading = false;
          this.applicationError =
            error.error?.message || 'Failed to submit application';
          this.showToastNotification('Failed to submit application', 'error');
          console.error('Application submit error:', error);
        },
      });
  }

  confirmWithdraw() {
    if (
      this.isApplicationLoading ||
      !this.applicationApplied ||
      this.applicationWithdrawn
    )
      return;
    this.showWithdrawConfirmModal = true;
  }

  cancelWithdraw() {
    this.showWithdrawConfirmModal = false;
  }

  withdrawApplication() {
    if (this.isApplicationLoading || !this.showWithdrawConfirmModal) return;

    this.isApplicationLoading = true;
    this.applicationError = '';

    const headers = {
      'x-client-id': this.surveyId,
      'Content-Type': 'application/json',
    };

    this.http
      .patch(
        `${environment.apiUrl}/application`,
        { withdrawn: true },
        { headers }
      )
      .subscribe({
        next: () => {
          this.isApplicationLoading = false;
          this.showWithdrawConfirmModal = false;
          this.applicationApplied = true;
          this.applicationWithdrawn = true;
          this.showToastNotification('Application withdrawn successfully');
        },
        error: (error) => {
          this.isApplicationLoading = false;
          this.applicationError =
            error.error?.message || 'Failed to withdraw application';
          this.showToastNotification('Failed to withdraw application', 'error');
          console.error('Application withdraw error:', error);
        },
      });
  }

  checkIfAnswersRecorded(): boolean {
    return (
      this.surveyData.q1 !== undefined ||
      this.surveyData.q2 !== undefined ||
      this.surveyData.q3 !== undefined ||
      this.surveyData.q4 !== undefined ||
      this.surveyData.q5 !== undefined ||
      (this.surveyData.q6 !== undefined && this.surveyData.q6 !== '')
    );
  }

  formatNumberWithCommas(value: number | undefined): string {
    if (value === undefined || value === null) {
      return '';
    }
    return value.toLocaleString();
  }

  formatCurrencyWithCommas(value: number | undefined): string {
    if (value === undefined || value === null) {
      return '';
    }
    return value.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  parseNumberFromFormatted(value: string): number {
    // Remove commas and parse as number
    const cleanValue = value.replace(/,/g, '');
    return parseInt(cleanValue, 10) || 0;
  }

  parseDecimalFromFormatted(value: string): number {
    // Remove commas and parse as decimal
    const cleanValue = value.replace(/,/g, '');
    const parsed = parseFloat(cleanValue);
    console.log('parseDecimalFromFormatted:', { value, cleanValue, parsed });
    // Return 0 if parsing fails, otherwise return the parsed value
    return isNaN(parsed) ? 0 : parsed;
  }

  onQuestionChange(question: keyof SurveyData, value: any) {
    this.surveyData[question] = value;
    this.updateSurvey();
  }

  onNumberInputChange(question: 'q2' | 'q5', event: Event) {
    const target = event.target as HTMLInputElement;
    const cursorPosition = target.selectionStart || 0;
    const oldValue = target.value;
    const newValue = target.value;

    if (newValue === '' || newValue === null || newValue === undefined) {
      this.surveyData[question] = undefined;
    } else {
      // Parse the formatted number (remove commas)
      if (question === 'q2') {
        this.surveyData[question] = this.parseNumberFromFormatted(newValue);
      } else if (question === 'q5') {
        // Use simple currency input handling
        this.onCurrencyInput(event);
        return;
      }
    }

    // Update the display with proper formatting
    setTimeout(() => {
      const formattedValue =
        question === 'q2'
          ? this.formatNumberWithCommas(this.surveyData[question])
          : this.formatCurrencyWithCommas(this.surveyData[question]);

      if (formattedValue !== newValue) {
        target.value = formattedValue;
        // Restore cursor position after formatting
        const newCursorPosition = this.calculateNewCursorPosition(
          oldValue,
          formattedValue,
          cursorPosition
        );
        target.setSelectionRange(newCursorPosition, newCursorPosition);
      }
    }, 0);

    this.updateSurvey();
  }

  onCurrencyClick(event: Event) {
    const target = event.target as HTMLInputElement;

    // Clear the field when clicked
    target.value = '';
    target.select();
  }

  onCurrencyInput(event: Event) {
    const target = event.target as HTMLInputElement;
    const value = target.value;

    // Only allow numbers and one decimal point
    const cleanValue = value.replace(/[^0-9.]/g, '');

    // Handle multiple decimal points - keep only the first one
    const decimalParts = cleanValue.split('.');
    const processedValue =
      decimalParts.length > 2
        ? decimalParts[0] + '.' + decimalParts.slice(1).join('')
        : cleanValue;

    // Update the display immediately (no survey data update during typing)
    target.value = processedValue;
  }

  onCurrencyBlur(event: Event) {
    const target = event.target as HTMLInputElement;
    const value = target.value;

    // If no decimal point, add .00
    let finalValue = value;
    if (value && !value.includes('.')) {
      finalValue = value + '.00';
    } else if (value && value.endsWith('.')) {
      finalValue = value + '00';
    } else if (
      value &&
      value.includes('.') &&
      value.split('.')[1].length === 1
    ) {
      finalValue = value + '0';
    }

    // Convert to number and format
    const numericValue = parseFloat(finalValue) || 0;
    this.surveyData.q5 = numericValue;

    // Format with commas
    const formattedValue = this.formatCurrencyWithCommas(numericValue);
    target.value = formattedValue;

    // Trigger update
    this.updateSurvey();
  }

  calculateNewCursorPosition(
    oldValue: string,
    newValue: string,
    oldCursorPosition: number
  ): number {
    // If the old value is empty, position at the end
    if (oldValue === '') {
      return newValue.length;
    }

    // Count commas before cursor in old value
    const commasBeforeCursor = (
      oldValue.substring(0, oldCursorPosition).match(/,/g) || []
    ).length;

    // Count commas in the entire new value
    const commasInNewValue = (newValue.match(/,/g) || []).length;

    // Calculate the position in the unformatted (no commas) version
    const oldUnformatted = oldValue.replace(/,/g, '');
    const newUnformatted = newValue.replace(/,/g, '');

    // Find the position in the unformatted old value
    const oldUnformattedPosition = oldCursorPosition - commasBeforeCursor;

    // If we're at the end of the old value, stay at the end
    if (oldUnformattedPosition >= oldUnformatted.length) {
      return newValue.length;
    }

    // Find the corresponding position in the new unformatted value
    let newUnformattedPosition = Math.min(
      oldUnformattedPosition,
      newUnformatted.length
    );

    // Count commas before this position in the new value
    let commasBeforeNewPosition = 0;
    for (let i = 0; i < newValue.length; i++) {
      if (newValue[i] === ',') {
        commasBeforeNewPosition++;
      }
      if (i - commasBeforeNewPosition >= newUnformattedPosition) {
        break;
      }
    }

    return Math.min(
      newUnformattedPosition + commasBeforeNewPosition,
      newValue.length
    );
  }

  onNumberInputKeyDown(question: 'q2' | 'q5', event: KeyboardEvent) {
    const target = event.target as HTMLInputElement;
    const cursorPosition = target.selectionStart || 0;
    const value = target.value;

    // Special handling for question 5 (currency) - use simple input handling
    if (question === 'q5') {
      // Allow normal input for currency - we'll handle formatting on input event
      return;
    }

    // Handle backspace when cursor is after a comma for q2
    if (event.key === 'Backspace' && cursorPosition > 0) {
      // Check if cursor is right after a comma
      if (value[cursorPosition - 1] === ',') {
        event.preventDefault();

        // Remove the digit before the comma and the comma
        const newValue =
          value.substring(0, cursorPosition - 2) +
          value.substring(cursorPosition);
        target.value = newValue;
        target.setSelectionRange(cursorPosition - 2, cursorPosition - 2);

        // Trigger the input change
        this.onNumberInputChange(question, event);
        return;
      }
    }

    // Allow only numbers, backspace, delete, arrow keys, tab, etc. for q2
    const allowedKeys = [
      'Backspace',
      'Delete',
      'ArrowLeft',
      'ArrowRight',
      'ArrowUp',
      'ArrowDown',
      'Tab',
      'Enter',
    ];

    if (
      !/[0-9]/.test(event.key) &&
      !allowedKeys.includes(event.key) &&
      !event.ctrlKey &&
      !event.metaKey
    ) {
      event.preventDefault();
    }
  }

  onNumberInputBlur(question: 'q2' | 'q5', event: Event) {
    const target = event.target as HTMLInputElement;
    const value = target.value;

    // Only handle q2, q5 is handled by onCurrencyBlur
    if (question === 'q2') {
      if (value === '' || value === null || value === undefined) {
        this.surveyData[question] = undefined;
      } else {
        // Parse the formatted number (remove commas)
        this.surveyData[question] = this.parseNumberFromFormatted(value);
      }

      // Update the display with proper formatting
      const formattedValue = this.formatNumberWithCommas(
        this.surveyData[question]
      );
      target.value = formattedValue;

      this.updateSurvey();
    }
  }

  onTextInputChange(question: keyof SurveyData, event: Event) {
    const target = event.target as HTMLTextAreaElement;

    if (
      target.value === '' ||
      target.value === null ||
      target.value === undefined
    ) {
      this.surveyData[question] = undefined;
    } else {
      // Only q6 accepts string values, others need type conversion
      if (question === 'q6') {
        this.surveyData[question] = target.value;
      } else {
        // For other questions, convert string to appropriate type
        const value = target.value;
        if (question === 'q1' || question === 'q3' || question === 'q4') {
          this.surveyData[question] = value === 'true' || value === '1';
        } else if (question === 'q2' || question === 'q5') {
          this.surveyData[question] = parseInt(value, 10) || 0;
        }
      }
    }
    this.updateSurvey();
  }

  onInputBlur(question: keyof SurveyData, event: Event) {
    // Trigger update when user moves away from any input
    this.updateSurvey();
  }

  cleanSurveyDataForSubmission(): SurveyData {
    // Ensure proper types before sending to backend
    return {
      q1: this.surveyData.q1,
      q2:
        this.surveyData.q2 !== undefined
          ? Number(this.surveyData.q2)
          : undefined,
      q3: this.surveyData.q3,
      q4: this.surveyData.q4,
      q5:
        this.surveyData.q5 !== undefined
          ? (() => {
              const result = parseFloat(this.surveyData.q5.toString());
              console.log('cleanSurveyDataForSubmission q5:', {
                original: this.surveyData.q5,
                result,
              });
              return result;
            })()
          : undefined,
      q6: this.surveyData.q6,
    };
  }

  updateSurvey() {
    if (this.surveySaveTimer) {
      clearTimeout(this.surveySaveTimer);
    }

    this.surveySaveTimer = setTimeout(() => {
      this.surveySaveTimer = null;
      this.sendSurveyUpdate();
    }, this.surveySaveDelayMs);
  }

  private sendSurveyUpdate() {
    if (!this.surveyId) {
      return;
    }

    this.isLoading = true;
    this.errorMessage = '';
    this.successMessage = '';

    const headers = {
      'x-client-id': this.surveyId,
      'Content-Type': 'application/json',
    };

    // Clean the data to ensure proper types
    const cleanedData = this.cleanSurveyDataForSubmission();

    this.http
      .patch(`${environment.apiUrl}/survey`, cleanedData, { headers })
      .subscribe({
        next: (response) => {
          this.isLoading = false;
          this.successMessage = 'Survey updated successfully!';
          this.hasRecordedAnswers = this.checkIfAnswersRecorded();
        },
        error: (error) => {
          this.isLoading = false;
          this.errorMessage = error.error?.message || 'Failed to update survey';
          console.error('Survey update error:', error);
        },
      });
  }
}
